import { LANGUAGES } from "@/lib/languages";
import type { Voice } from "@/hooks/api/use-voices";

/** One language's curated list, in the order clients are offered it. */
export interface LanguageGroup {
	language: string;
	voices: Voice[];
}

/**
 * The catalogue split by language, ours first.
 *
 * Grouped on the exact tag rather than the primary subtag: `en` and `en-SG` are two curated lists
 * that a super-admin maintains separately, and folding them together here would let a drag inside
 * one silently renumber the other. The looser match — `en` answering an `en-SG` agent — belongs to
 * the client catalogue and lives in `languageMatches`.
 */
export function groupByLanguage(voices: Voice[]): LanguageGroup[] {
	const groups = new Map<string, Voice[]>();

	for (const voice of voices) {
		const existing = groups.get(voice.language);
		if (existing) existing.push(voice);
		else groups.set(voice.language, [voice]);
	}

	const order = LANGUAGES.map((language) => language.value);

	return [...groups.entries()]
		.map(([language, rows]) => ({ language, voices: rows }))
		.toSorted((left, right) => {
			const leftRank = order.indexOf(left.language);
			const rightRank = order.indexOf(right.language);

			// Anything we do not run today sorts after the languages we do, alphabetically.
			if (leftRank === -1 && rightRank === -1) return left.language.localeCompare(right.language);
			if (leftRank === -1) return 1;
			if (rightRank === -1) return -1;
			return leftRank - rightRank;
		});
}

/** Where a newly curated voice goes: after everything already offered in its language. */
export function nextPosition(rows: Voice[], language: string): number {
	return rows.reduce((last, voice) => (voice.language === language ? Math.max(last, voice.position + 1) : last), 0);
}

/** The flat list with one language's rows put in the order just chosen, everything else untouched. */
export function reorderWithin(rows: Voice[], language: string, ids: string[]): Voice[] {
	const byId = new Map(rows.map((voice) => [voice.id, voice]));
	const ordered = ids.flatMap((id) => {
		const voice = byId.get(id);
		return voice && voice.language === language ? [voice] : [];
	});

	// An id naming nothing is a stale tab dragging a voice somebody else deleted. The rows it left
	// unnamed keep their own order behind the named ones rather than dropping off the screen —
	// filling the gap with the row already in that slot would render one voice twice and lose
	// another until the next refetch.
	const named = new Set(ordered.map((voice) => voice.id));
	const queue = [...ordered, ...rows.filter((voice) => voice.language === language && !named.has(voice.id))];

	let cursor = 0;

	return rows.map((voice) => {
		if (voice.language !== language) return voice;
		const next = queue[cursor] ?? voice;
		cursor += 1;
		return next;
	});
}
